import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, ChevronLeft, ChevronRight, Clock, Gauge, KeyRound, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { odstep } from '@/lib/odswiezanie';
import { fetchRuchPrzeglad, fetchRuchZadania, type RuchPrzeglad } from '@/lib/ruch-api';

/**
 * RUCH POWIERZCHNI `/api/v1` — co przechodzi przez integrację i z jakim skutkiem.
 *
 * ⚠️ POWSTAŁO ZE ZGŁOSZENIA (2026-08-18): Vending+ przez pół dnia dostawał `403` na dwóch trasach,
 * a u nas nie było tego widać NIGDZIE. Lista tokenów mówi „ostatnio użyty" i licznik użyć, czyli
 * „coś dzwoniło" — a pytania, które zadaje się realnie, brzmią: czy dzwoni regularnie, co
 * odpowiadamy, ile to trwa, która trasa się wywala i z jakiego adresu.
 *
 * ⚠️ WYKRES RYSOWANY SVG, BEZ BIBLIOTEKI. Czterdzieści osiem słupków z dwoma wartościami to
 * kilkanaście linii; dociąganie biblioteki do wykresów kosztowałoby więcej niż cała ta sekcja
 * i weszłoby do każdego wydania portalu na zawsze.
 */

/** Kafel z jedną liczbą i jednym zdaniem pod nią — jak na ekranach floty. */
function Kafel({
  ikona: Ikona,
  etykieta,
  wartosc,
  pod,
  ostrzega,
}: {
  ikona: typeof Activity;
  etykieta: string;
  wartosc: string;
  pod?: string;
  ostrzega?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Ikona className="size-3.5" />
        {etykieta}
      </p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${ostrzega === true ? 'text-[var(--warning)]' : ''}`}>
        {wartosc}
      </p>
      {pod !== undefined && <p className="text-[11px] text-muted-foreground">{pod}</p>}
    </div>
  );
}

/**
 * WYKRES 48 GODZIN — zielone poprawne, czerwone błędne, jeden słupek na godzinę.
 *
 * ⚠️ GODZINY BEZ RUCHU ZOSTAJĄ PUSTE, ale są. Pominięcie ich ścisnęłoby wykres tak, że przerwa
 * w pracy integracji — czyli jedyna rzecz, której się tu szuka — przestałaby być widoczna.
 *
 * ⚠️ SKALA OD NAJWYŻSZEJ GODZINY, nie od stałej wartości: ruch bywa różny na różnych instancjach,
 * a stała skala robi z małego ruchu płaską kreskę, po której nie da się nic poznać.
 */
function Wykres({ dane }: { dane: RuchPrzeglad['wykres'] }) {
  const { t } = useI18n();
  const maks = Math.max(1, ...dane.map((d) => d.ok + d.bledy));
  const szerokosc = 100 / dane.length;

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('ruch.chartTitle')}</p>
        <p className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-[var(--success)]" /> {t('ruch.ok')}
          </span>
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-destructive" /> {t('ruch.errors')}
          </span>
        </p>
      </div>
      <svg viewBox="0 0 100 30" className="h-28 w-full" preserveAspectRatio="none" role="img">
        {dane.map((d, i) => {
          const razem = d.ok + d.bledy;
          const wysokosc = (razem / maks) * 28;
          const bledy = razem === 0 ? 0 : (d.bledy / razem) * wysokosc;
          const x = i * szerokosc;
          const w = Math.max(szerokosc - 0.35, 0.3);
          return (
            <g key={d.godzina}>
              <rect x={x} y={30 - wysokosc} width={w} height={Math.max(wysokosc - bledy, 0)} fill="var(--success)" />
              {bledy > 0 && <rect x={x} y={30 - bledy} width={w} height={bledy} fill="var(--destructive)" />}
              {/* Podpowiedź na słupku — inaczej wykres mówi „mniej więcej", a przy diagnozie liczy się liczba. */}
              <title>{`${new Date(d.godzina).toLocaleString()} · ${t('ruch.ok')}: ${d.ok} · ${t('ruch.errors')}: ${d.bledy}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{new Date(dane[0]?.godzina ?? Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        <span>{t('ruch.now')}</span>
      </div>
    </div>
  );
}

const KOLOR_STATUSU = (status: number): 'success' | 'warning' | 'destructive' =>
  status < 400 ? 'success' : status < 500 ? 'warning' : 'destructive';

export function RuchApiSection() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [metoda, setMetoda] = useState('');
  const [status, setStatus] = useState('');
  const [ip, setIp] = useState('');
  const [strona, setStrona] = useState(1);

  /**
   * ⚠️ ZMIANA FILTRA WRACA NA PIERWSZĄ STRONĘ. Bez tego wybranie statusu przy otwartej stronie
   * czwartej daje pustą tabelę — wygląda to jak „nie ma takich wpisów", a znaczy „nie ma ich
   * na czwartej stronie".
   */
  const ustawFiltr = (co: 'metoda' | 'status' | 'ip', wartosc: string) => {
    setStrona(1);
    if (co === 'metoda') setMetoda(wartosc);
    else if (co === 'status') setStatus(wartosc);
    else setIp(wartosc);
  };

  /** Ta sekcja jest narzędziem naszej obsługi — konta zawężone do klienta nie mają tu wstępu. */
  const isSdi = user?.clientId === null;

  const przeglad = useQuery({
    queryKey: ['ruch-api', 'przeglad'],
    queryFn: fetchRuchPrzeglad,
    enabled: isSdi,
    /** Ruch integracji zmienia się co kilkanaście minut — spokojne tempo w zupełności wystarcza. */
    refetchInterval: odstep(false),
  });

  const zadania = useQuery({
    queryKey: ['ruch-api', 'zadania', metoda, status, ip, strona],
    queryFn: () =>
      fetchRuchZadania({
        metoda: metoda === '' ? undefined : metoda,
        status: status === '' ? undefined : Number(status),
        ip: ip === '' ? undefined : ip,
        page: strona,
      }),
    enabled: isSdi,
    refetchInterval: odstep(false),
  });

  if (!isSdi) return null;

  const k = przeglad.data?.kafle;
  const wybor = 'rounded-md border border-border bg-card px-2 py-1.5 text-sm';

  return (
    <section className="mt-6 space-y-3 rounded-lg border border-border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Activity className="size-4 text-primary" />
          {t('ruch.title')}
        </h2>
      </div>

      {przeglad.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
        </p>
      )}

      {k !== undefined && (
        <>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Kafel
              ikona={Activity}
              etykieta={t('ruch.req24h')}
              wartosc={String(k.zapytan24h)}
              pod={t('ruch.perHour').replace('{n}', String(Math.round((k.zapytan24h / 24) * 10) / 10))}
            />
            <Kafel
              ikona={KeyRound}
              etykieta={t('ruch.perToken')}
              wartosc={k.naToken24h === null ? '—' : String(k.naToken24h)}
              pod={t('ruch.tokensActive').replace('{n}', String(k.tokenow24h))}
            />
            <Kafel
              ikona={Gauge}
              etykieta={t('ruch.req7d')}
              wartosc={String(k.zapytan7dni)}
              pod={t('ruch.perDay').replace('{n}', String(Math.round((k.zapytan7dni / 7) * 10) / 10))}
            />
            <Kafel
              ikona={AlertTriangle}
              etykieta={t('ruch.err24h')}
              wartosc={String(k.bledy24h)}
              pod={t('ruch.errShare').replace('{p}', String(k.procentBledow24h))}
              /** Kolor tylko przy niezerowych błędach — stałe ostrzeżenie przestaje być czytane. */
              ostrzega={k.bledy24h > 0}
            />
            <Kafel
              ikona={Clock}
              etykieta={t('ruch.avgTime')}
              wartosc={k.sredniCzasOk24h === null ? '—' : `${k.sredniCzasOk24h} ms`}
              pod={t('ruch.avgTimeWhat')}
            />
          </div>

          <Wykres dane={przeglad.data!.wykres} />

          <div className="rounded-lg border border-border">
            <p className="border-b border-border px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              {t('ruch.endpoints').replace('{n}', String(przeglad.data!.endpointy.length))}
            </p>
            {przeglad.data!.endpointy.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted-foreground">{t('ruch.empty')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5">{t('ruch.colEndpoint')}</th>
                    <th className="px-3 py-1.5 text-right">{t('ruch.colCalls')}</th>
                    <th className="px-3 py-1.5 text-right">{t('ruch.ok')}</th>
                    <th className="px-3 py-1.5 text-right">{t('ruch.errors')}</th>
                    <th className="px-3 py-1.5 text-right">{t('ruch.colAvg')}</th>
                    <th className="px-3 py-1.5">{t('ruch.colLastStatus')}</th>
                    <th className="px-3 py-1.5">{t('ruch.colLast')}</th>
                  </tr>
                </thead>
                <tbody>
                  {przeglad.data!.endpointy.map((e) => (
                    <tr key={`${e.method} ${e.route}`} className="border-t border-border/60">
                      <td className="px-3 py-1.5">
                        <span className="mr-1.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase">
                          {e.method}
                        </span>
                        <code className="font-mono text-xs">{e.route.replace('/api/v1', '')}</code>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{e.wywolania}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-[var(--success)]">{e.ok}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${e.bledy > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {e.bledy}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                        {e.sredniCzas === null ? '—' : `${e.sredniCzas} ms`}
                      </td>
                      <td className="px-3 py-1.5">
                        <Badge variant={KOLOR_STATUSU(e.ostatniStatus)}>{e.ostatniStatus}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">{new Date(e.ostatnio).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/*
        ⚠️ WARTOŚCI FILTRÓW POCHODZĄ Z DANYCH, nie z listy wpisanej w kodzie — inaczej ekran
        oferowałby wybory dające pustą tabelę (np. `DELETE`, którego integracja nigdy nie woła).
      */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-[11px] text-muted-foreground">
          {t('ruch.filterMethod')}
          <select className={wybor} value={metoda} onChange={(e) => ustawFiltr('metoda', e.target.value)}>
            <option value="">{t('ruch.all')}</option>
            {(zadania.data?.metody ?? []).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-muted-foreground">
          {t('ruch.filterStatus')}
          <select className={wybor} value={status} onChange={(e) => ustawFiltr('status', e.target.value)}>
            <option value="">{t('ruch.all')}</option>
            {(zadania.data?.statusy ?? []).map((s) => (
              <option key={s} value={String(s)}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-muted-foreground">
          {t('ruch.filterIp')}
          <select className={wybor} value={ip} onChange={(e) => ustawFiltr('ip', e.target.value)}>
            <option value="">{t('ruch.all')}</option>
            {(zadania.data?.adresy ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        {zadania.isFetching && <Loader2 className="mb-2 size-4 animate-spin text-muted-foreground" />}
      </div>

      <div className="max-h-96 overflow-y-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5">{t('ruch.colTime')}</th>
              <th className="px-3 py-1.5">{t('ruch.colRequest')}</th>
              <th className="px-3 py-1.5">{t('ruch.colToken')}</th>
              <th className="px-3 py-1.5">{t('ruch.colIp')}</th>
              <th className="px-3 py-1.5 text-right">{t('ruch.colDuration')}</th>
              <th className="px-3 py-1.5">{t('ruch.colResult')}</th>
            </tr>
          </thead>
          <tbody>
            {(zadania.data?.rows ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-3 text-sm text-muted-foreground">
                  {t('ruch.empty')}
                </td>
              </tr>
            )}
            {(zadania.data?.rows ?? []).map((r, i) => (
              <tr key={`${r.at}-${i}`} className="border-t border-border/60">
                <td className="px-3 py-1.5 text-muted-foreground">{new Date(r.at).toLocaleString()}</td>
                <td className="px-3 py-1.5">
                  <span className="mr-1.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase">{r.method}</span>
                  <code className="font-mono text-xs">{r.route.replace('/api/v1', '')}</code>
                </td>
                {/* Etykieta tokenu, nie UUID — „Vending+" mówi wszystko, identyfikator nic. */}
                <td className="px-3 py-1.5 text-muted-foreground">{r.tokenLabel ?? t('ruch.noToken')}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{r.ip ?? '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{r.durationMs} ms</td>
                <td className="px-3 py-1.5">
                  <Badge variant={KOLOR_STATUSU(r.status)}>{r.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        ⚠️ LICZBA WSZYSTKICH WPISÓW JEST TU NAJWAŻNIEJSZA, nie same strzałki. Pierwsza wersja
        oddawała najnowsze 200 wierszy i milczała o resztę — przy trzech tysiącach wpisów ekran
        wyglądał identycznie jak przy dwustu, więc nikt nie miał powodu szukać głębiej.
      */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {t('ruch.pageOf')
            .replace('{od}', String(zadania.data === undefined ? 0 : (zadania.data.page - 1) * zadania.data.pageSize + 1))
            .replace('{do}', String(zadania.data === undefined ? 0 : (zadania.data.page - 1) * zadania.data.pageSize + zadania.data.rows.length))
            .replace('{total}', String(zadania.data?.total ?? 0))}
        </span>
        <span className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            disabled={strona <= 1 || zadania.isFetching}
            onClick={() => setStrona((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="size-4" />
            {t('ruch.prev')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            /** Następna aktywna tylko wtedy, gdy naprawdę jest co pokazać — inaczej klik daje pustą tabelę. */
            disabled={zadania.data === undefined || zadania.data.page * zadania.data.pageSize >= zadania.data.total || zadania.isFetching}
            onClick={() => setStrona((p) => p + 1)}
          >
            {t('ruch.next')}
            <ChevronRight className="size-4" />
          </Button>
        </span>
      </div>
    </section>
  );
}
